import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending"); // pending | paid | expired | failed
  const [amount, setAmount] = useState(null);
  const { clear } = useCart();
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 7;
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setStatus("paid");
          setAmount(data.amount_total);
          if (!polledRef.current) {
            polledRef.current = true;
            clear();
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          setStatus("failed");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="px-6 md:px-12 lg:px-20 py-32 max-w-2xl mx-auto text-center" data-testid="page-checkout-success">
      {status === "pending" && (
        <div data-testid="checkout-status-pending">
          <Loader2 size={36} className="mx-auto text-[#3B4A3F] animate-spin" />
          <h1 className="font-serif-display text-4xl text-[#1A1F1B] mt-6">
            Confirming your order…
          </h1>
          <p className="mt-3 text-[#4E5C50]">This will only take a moment.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="checkout-status-paid">
          <CheckCircle2 size={40} className="mx-auto text-[#3B4A3F]" />
          <div className="label-eyebrow mt-6">Order Confirmed</div>
          <h1 className="font-serif-display text-5xl text-[#1A1F1B] mt-3">
            Thank you.
          </h1>
          <p className="mt-4 text-[#4E5C50] max-w-md mx-auto leading-relaxed">
            Your piece will be carefully wrapped in the studio.{" "}
            {amount && <>You were charged ${(amount / 100).toFixed(2)}.</>} A
            confirmation has been sent via Stripe.
          </p>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/shop" className="btn-secondary">Keep browsing</Link>
            <Link to="/" className="btn-primary">Back home</Link>
          </div>
        </div>
      )}
      {(status === "expired" || status === "failed") && (
        <div data-testid="checkout-status-failed">
          <AlertCircle size={40} className="mx-auto text-[#A39171]" />
          <h1 className="font-serif-display text-4xl text-[#1A1F1B] mt-6">
            We couldn't confirm your order.
          </h1>
          <p className="mt-3 text-[#4E5C50]">
            If you were charged, please reach out and we'll sort it out.
          </p>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/shop" className="btn-secondary">Return to Shop</Link>
            <Link to="/contact" className="btn-primary">Contact Studio</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutSuccess;
